import { Dispatch } from 'react-redux';
import { TextData } from 'notes-editor';
import hashHistory from 'react-router/lib/hashHistory';

import { Action } from '../../actions';
import { StoreState } from '../../reducers';
import { createNotification } from '../NotificationBar/actions';
import { removeFromSelection, SelectionType } from '../Selection/actions';
import { MenuActionKeys } from '../MenuManager/actions';
import { Api } from '../../typings/api';
import Utils from '../../utils';
import ApiUtils from '../../utils/api';
import UserAgentUtils from '../../utils/userAgent';
import IndexedDBManager from '../../utils/indexedDB/manager';
import RoutesUtils from '../../utils/routes';
import { RoutePath } from '../../routes/constants';
import { NotesStatus, NoteState, LoadingStatus } from './reducer';
import store from '../../store';

export enum NotesActionKeys {
	GET = '@@notes/GET',
	SET = '@@notes/SET',
	RESET = '@@notes/RESET',
	CREATE = '@@notes/CREATE',
	UPDATE = '@@notes/UPDATE',
	REMOVE = '@@notes/REMOVE',
	SET_LOADING_STATUS = '@@notes/SET_LOADING_STATUS',
	SET_STATUS = '@@notes/SET_STATUS',
	TOGGLE_EDITOR = '@@notes/TOGGLE_EDITOR',
}

interface GetNotesAction extends Action {
	type: NotesActionKeys.GET;
}

interface SetNotesAction extends Action {
	type: NotesActionKeys.SET;
	items: NoteState[];
	sourceType: NotesStatus;
}

interface ResetNotesAction extends Action {
	type: NotesActionKeys.RESET;
	sourceType: NotesStatus;
}

interface CreateNoteAction extends Action {
	type: NotesActionKeys.CREATE;
	id: number;
	data: NoteState;
	sourceType: NotesStatus;
}

interface UpdateNoteAction extends Action {
	type: NotesActionKeys.UPDATE;
	id: number;
	data: NoteState;
	sourceType: NotesStatus;
}

interface RemoveNoteAction extends Action {
	type: NotesActionKeys.REMOVE;
	id: number;
	sourceType: NotesStatus;
}

interface SetLoadingStatusAction extends Action {
	type: NotesActionKeys.SET_LOADING_STATUS;
	status: LoadingStatus;
}

interface SetNotesStatusAction extends Action {
	type: NotesActionKeys.SET_STATUS;
	status: NotesStatus;
}

interface ToggleNotesEditorAction extends Action {
	type: NotesActionKeys.TOGGLE_EDITOR;
	id?: number;
}

export type NotesAction =
	GetNotesAction |
	SetNotesAction |
	ResetNotesAction |
	CreateNoteAction |
	UpdateNoteAction |
	RemoveNoteAction |
	SetLoadingStatusAction |
	SetNotesStatusAction |
	ToggleNotesEditorAction;

const setNotes = (items: NoteState[], sourceType: NotesStatus): SetNotesAction => ({
	type: NotesActionKeys.SET,
	items,
	sourceType,
});

export const setLoadingStatus = (status: LoadingStatus): SetLoadingStatusAction => ({
	type: NotesActionKeys.SET_LOADING_STATUS,
	status,
});

export const setNotesStatus = (status: NotesStatus): SetNotesStatusAction => ({
	type: NotesActionKeys.SET_STATUS,
	status,
});

export const toggleNotesEditor = (id?: number) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const action: ToggleNotesEditorAction = {
		type: NotesActionKeys.TOGGLE_EDITOR,
		id,
	};
	dispatch(action);

	const { menuManager: { leftMenuIsOpen } } = getState();

	if (leftMenuIsOpen) {
		dispatch({ type: MenuActionKeys.REQUEST_CLOSE_LEFT_MENU });
	}
};

export const getNotes = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		dispatch(setLoadingStatus(LoadingStatus.LOADING));
		const localNotes = await IndexedDBManager.getNotes();
		// not to store a big amount of data in redux store
		const simplifiedLocalNotes = localNotes
			.filter(({ id }) => !Utils.isSharedNote(id))
			.map(({ content, drawing, ...restData }) => ({
				...restData,
				hasImage: !!drawing,
			}));
		dispatch(setNotes(simplifiedLocalNotes, NotesStatus.LOCAL));

		const isOnline = await UserAgentUtils.isOnlinePromise();

		if (isOnline) {
			const { notes: sharedNotes } = await ApiUtils.getNotes();
			await IndexedDBManager.setNotes(sharedNotes);
			const simplifiedSharedNotes = sharedNotes.map(({ content, drawing, ...restData }) => ({
				...restData,
				hasImage: !!drawing,
			}));
			dispatch(setNotes(simplifiedSharedNotes, NotesStatus.SHARED));
		} else {
			dispatch(createNotification('info.offline_fetch_notes'));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_notes_error'));
	} finally {
		dispatch(setLoadingStatus(LoadingStatus.LOADED));
	}
};

export const resetNotes = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { notes, selection } = getState();
	const sharedNotesIds = Object.keys(notes.SHARED);

	dispatch({
		type: NotesActionKeys.RESET,
		sourceType: NotesStatus.LOCAL,
	});
	dispatch({
		type: NotesActionKeys.RESET,
		sourceType: NotesStatus.SHARED,
	});

	// remove not selected shared notes from indexedDB
	for (let i = 0; i < sharedNotesIds.length; i++) {
		const noteId = +sharedNotesIds[i];
		const isSelectedNote = selection.notes.includes(noteId);

		if (!isSelectedNote) {
			await IndexedDBManager.removeNote(noteId);
		}
	}
};

export const createNote = (title: string, { formattedText }: TextData, drawing: string) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const date = Date.now();
		const id = -date; // minus is used to distinguish with notes saved on server
		const data: Api.NoteData = {
			id,
			content: formattedText,
			title,
			drawing,
			date,
		};
		IndexedDBManager.setNote(id, data);

		const {
			content: contentToRemove,
			drawing: drawingToRemove,
			...restData
		} = data;
		const action: CreateNoteAction = {
			type: NotesActionKeys.CREATE,
			id,
			data: {
				...restData,
				hasImage: !!drawing,
			},
			sourceType: NotesStatus.LOCAL,
		};
		dispatch(action);

		if (!isNotesPageOpened()) {
			dispatch(createNotification('info.create_note_success', {
				url: `#${RoutesUtils.getPathNotes()}${hashHistory.getCurrentLocation().search}`
			}));
		}
	} catch (error) {
		dispatch(createNotification('error.cant_create_note'));
	}
};

export const updateNote = (id: number, data: Api.NoteData, shouldUpdateDate = true) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const { notes: { currentStatus } } = getState();

		const dataToUpdate = {
			...data,
			id,
		} as Api.NoteData;

		if (shouldUpdateDate && !Utils.isSharedNote(id)) {
			dataToUpdate.date = Date.now();
		}

		if (Utils.isSharedNote(id)) {
			dispatch(setLoadingStatus(LoadingStatus.SAVING));
			const response = await ApiUtils.saveNote(dataToUpdate);
			const { modifiedOn } = response.notes[0];
			dataToUpdate.modifiedOn = modifiedOn;
		}

		IndexedDBManager.updateNote(id, dataToUpdate);

		if (isNotesPageOpened()) {
			const {
				content,
				drawing,
				...restData
			} = dataToUpdate;
			const action: UpdateNoteAction = {
				type: NotesActionKeys.UPDATE,
				id,
				data: {
					...restData,
					hasImage: !!drawing,
				},
				sourceType: currentStatus,
			};
			dispatch(action);
		}
	} catch (error) {
		dispatch(createNotification('error.cant_update_note'));
	} finally {
		dispatch(setLoadingStatus(LoadingStatus.LOADED));
	}
};

export const removeNote = (id: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const { notes: { currentStatus } } = getState();
		const isSharedNote = currentStatus === NotesStatus.SHARED;

		const action: RemoveNoteAction = {
			type: NotesActionKeys.REMOVE,
			id,
			sourceType: currentStatus,
		};
		dispatch(action);

		dispatch(removeFromSelection([id], SelectionType.NOTES));

		if (isSharedNote) {
			await ApiUtils.removeNote(id);
		}

		await IndexedDBManager.removeNote(id);
	} catch (error) {
		dispatch(createNotification('error.cant_remove_note'));
	}
};

const isNotesPageOpened = (): boolean => {
	const { routerParamsHistory } = store.getState();
	const currentRoute = RoutesUtils.getCurrentRoute(routerParamsHistory);
	return currentRoute.includes(RoutePath.NotesPage);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/NotesPage/actions.ts